import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { API_BASE_URL } from '../api'; // Importamos la URL del backend

const compressImage = (src, quality = 0.5, maxWidth = 800) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        if (typeof src === 'string' && src.startsWith('http')) {
            img.crossOrigin = "anonymous";
        }
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                
                if (width > maxWidth) {
                    height = (maxWidth / width) * height;
                    width = maxWidth;
                }
                
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                const dataUrl = canvas.toDataURL('image/jpeg', quality);
                resolve(dataUrl);
            } catch (err) {
                reject(err);
            }
        };
        img.onerror = (e) => reject(e);
        img.src = src;
    });
};

export const generateDefenseReport = async (trip, evidences, positions, mapImage) => {
    const doc = new jsPDF({ compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // --- FUNCIONES AUXILIARES ---
    const loadImage = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            // Ya no necesitamos anonymous porque viene de nuestro propio servidor (proxy)
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
            img.src = url;
        });
    };

    // --- ENCABEZADO Y LOGO ---
    try {
        const logo = await loadImage('/logoe.png');
        doc.addImage(logo, 'PNG', 15, 10, 30, 15);
    } catch (e) {
        console.warn("Logo no disponible para PDF");
    }

    doc.setFontSize(18);
    doc.setTextColor(0, 51, 153);
    doc.text("INFORME DE CUMPLIMIENTO DE SERVICIO", 50, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("EmiGo Transport - Certificación de Auditoría GPS", 50, 26);

    // --- INFORMACIÓN DEL VIAJE (CAJA DE RESUMEN) ---
    doc.setDrawColor(200);
    doc.setFillColor(245, 245, 245);
    doc.rect(15, 35, pageWidth - 30, 45, 'F');
    
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.setFont("helvetica", "bold");
    doc.text("DATOS DE LA RESERVA", 20, 42);
    
    doc.setFont("helvetica", "normal");
    doc.text(`Reserva (O.S.):`, 20, 50);
    doc.setFont("helvetica", "bold");
    doc.text(trip.external_reference || "N/A", 55, 50);
    
    doc.setFont("helvetica", "normal");
    doc.text(`Pasajero:`, 20, 57);
    doc.text(trip.passenger_name, 55, 57);
    
    doc.setFont("helvetica", "normal");
    doc.text(`Fecha Servicio:`, 20, 64);
    doc.text(format(new Date(trip.scheduled_time), "dd/MM/yyyy"), 55, 64);

    doc.text(`Origen:`, 20, 71);
    doc.setFontSize(9);
    doc.text(trip.pickup_address, 55, 71, { maxWidth: 130 });
    
    doc.setFontSize(11);
    doc.text(`Destino:`, 20, 78);
    doc.setFontSize(9);
    doc.text(trip.dropoff_address, 55, 78, { maxWidth: 130 });

    // --- IMAGEN DEL MAPA (PRUEBA RECORRIDO) ---
    let startTimelineY = 100;
    if (mapImage) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("AUDITORÍA DE RECORRIDO GPS (RUTA REALIZADA)", 15, 95);
        // El mapa está en Base64, lo comprimimos a JPEG
        let compressedMap = mapImage;
        try {
            compressedMap = await compressImage(mapImage, 0.6, 900);
        } catch (e) {
            console.warn("Error compressing map image, using original:", e);
        }
        doc.addImage(compressedMap, 'JPEG', 15, 100, pageWidth - 30, 75, undefined, 'FAST');
        startTimelineY = 185; 
    }

    // --- LÍNEA DE TIEMPO (TABLA DE HITOS) ---
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("HITOS DE EJECUCIÓN (AUDITORÍA GPS)", 15, startTimelineY - 5);

    const timelineData = [
        ["Estado", "Fecha y Hora (Local)", "Certificación"],
        ["PROGRAMADO", format(new Date(trip.scheduled_time), "HH:mm 'hrs'"), "Planificación de Sistema"],
        ["INICIO RUTA", trip.gps_en_route_time ? format(new Date(trip.gps_en_route_time), "HH:mm:ss") : "N/R", "Validación GPS del Conductor"],
        ["LLEGADA ORIGEN", trip.gps_arrived_time ? format(new Date(trip.gps_arrived_time), "HH:mm:ss") : "N/R", "Validación de Cercanía (Geofence)"],
        ["PASAJERO A BORDO", trip.start_time ? format(new Date(trip.start_time), "HH:mm:ss") : "N/R", "Validación de Inicio de Viaje"],
        ["FINALIZADO", trip.end_time ? format(new Date(trip.end_time), "HH:mm:ss") : "N/R", "Confirmación de Término"]
    ];

    autoTable(doc, {
        startY: startTimelineY,
        head: [timelineData[0]],
        body: timelineData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [0, 51, 153] },
        styles: { fontSize: 9 }
    });

    // --- PANEL DE EVIDENCIAS FOTOGRÁFICAS ---
    let lastY = doc.lastAutoTable.finalY + 15;
    
    if (evidences && evidences.length > 0) {
        // MEJORA: Si queda menos de 80mm de espacio, saltamos de página antes de poner las fotos
        if (lastY > 200) {
            doc.addPage();
            lastY = 20;
        }

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("EVIDENCIA VISUAL (EMIGO LENS)", 15, lastY);
        lastY += 10;

        const photosToShow = evidences.slice(0, 4);
        let photoWidth = pageWidth - 30; // Margen de 15 a cada lado
        let photoHeight = photoWidth * 0.75;

        for (let i = 0; i < photosToShow.length; i++) {
            // Si la foto no cabe en la página actual, saltamos de página
            if (lastY + photoHeight > 260) {
                doc.addPage();
                lastY = 20;
            }

            try {
                const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;
                const proxyUrl = `${baseUrl}transport/evidence/proxy/${photosToShow[i].id}`;
                
                const compressedPhoto = await compressImage(proxyUrl, 0.5, 800);
                doc.addImage(compressedPhoto, 'JPEG', 15, lastY, photoWidth, photoHeight, undefined, 'FAST');
                
                doc.setFontSize(10);
                const captureTime = format(new Date(photosToShow[i].captured_at), "HH:mm:ss");
                doc.text(`Captura GPS: ${captureTime} hrs`, 15, lastY + photoHeight + 7);
                
                lastY += photoHeight + 15; // Espacio para la siguiente foto
            } catch (e) {
                doc.rect(15, lastY, photoWidth, photoHeight);
                doc.setFontSize(10);
                doc.text("Error recuperando imagen", 20, lastY + (photoHeight/2));
                lastY += photoHeight + 15;
            }
        }
    }

    // --- PIE DE PÁGINA ---
    const pageCount = doc.internal.getNumberOfPages();
    for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
            `Este documento certifica la ejecución del servicio mediante datos telemáticos inalterables registrados en los servidores de EmiGo Transport.`,
            pageWidth / 2, 285, { align: 'center' }
        );
        doc.text(`Página ${i} de ${pageCount}`, pageWidth - 25, 285);
    }

    doc.save(`Defensa_Reserva_${trip.external_reference || trip.id}.pdf`);
};